import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type Platform = 'chrome' | 'ios-safari' | 'other';

function detectPlatform(): Platform {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua) && !/CriOS/.test(ua);
  if (isIOS && isSafari) return 'ios-safari';
  if (/Chrome/.test(ua) && !isIOS) return 'chrome';
  return 'other';
}

const PWAInstallBanner: React.FC = () => {
  const { i18n } = useTranslation();
  const isHindi = i18n.language === 'hi';
  const platform = useRef(detectPlatform());

  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    // Already running as installed PWA (standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches) {
      // They opened from home screen - they definitely installed it
      localStorage.setItem('pwa-installed', 'true');
      return;
    }
    // Previously confirmed installed (opened in standalone at least once)
    if (localStorage.getItem('pwa-installed') === 'true') return;

    // Dismissed recently
    const dismissedAt = localStorage.getItem('pwa-dismiss-at');
    if (dismissedAt) {
      const days = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (days < 7) return;
    }

    if (platform.current === 'chrome') {
      // Chrome: wait for native prompt only - don't show fallback
      const handler = (e: Event) => {
        e.preventDefault();
        setInstallPrompt(e as BeforeInstallPromptEvent);
        setShowBanner(true);
      };
      window.addEventListener('beforeinstallprompt', handler);
      window.addEventListener('appinstalled', () => {
        setShowBanner(false);
        trackInstall();
      });
      return () => window.removeEventListener('beforeinstallprompt', handler);

    } else if (platform.current === 'ios-safari') {
      // iOS Safari: show instructions after 3 seconds
      const timer = setTimeout(() => setShowBanner(true), 3000);
      return () => clearTimeout(timer);

    } else {
      // Firefox, Samsung Browser, etc: show after 5 seconds
      const timer = setTimeout(() => setShowBanner(true), 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  const trackInstall = () => {
    localStorage.setItem('pwa-installed', 'true');
    try {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      w.gtag?.('event', 'pwa_install', {
        event_category: 'engagement',
        event_label: 'pwa_installed',
      });
    } catch {
      // GA not loaded
    }
  };

  const handleInstall = async () => {
    if (installPrompt) {
      // Chrome native install
      await installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === 'accepted') {
        trackInstall();
        setShowBanner(false);
      }
      setInstallPrompt(null);
    } else {
      // Show platform-specific instructions
      setShowInstructions(true);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-dismiss-at', String(Date.now()));
    setShowBanner(false);
    setShowInstructions(false);
  };

  if (!showBanner) return null;

  return (
    <div data-client-only className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up">
      <div className="max-w-md mx-auto bg-cosmic-900 border border-divine-500/30 rounded-xl p-4 shadow-lg shadow-divine-500/10 backdrop-blur-sm">

        {!showInstructions ? (
          <>
            <div className="flex items-start gap-3">
              <img
                src="/logo192.png"
                alt="Bhairav Aaradhyaa"
                className="w-12 h-12 rounded-xl flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <h3 className="text-cream font-semibold text-sm">
                  Bhairav Aaradhyaa
                </h3>
                <p className="text-cream/60 text-xs mt-0.5 leading-relaxed">
                  Install the app for offline stotras and daily practice reminders
                </p>
                {isHindi && (
                  <p className="text-cream/35 text-[10px] mt-0.5">
                    Offline stotras aur daily sadhana reminder ke liye app install karo
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleDismiss}
                className="flex-1 px-3 py-2 text-xs text-cream/50 hover:text-cream/80 transition-colors rounded-lg"
              >
                Later
              </button>
              <button
                onClick={handleInstall}
                className="flex-1 px-3 py-2 text-xs font-semibold bg-divine-500 text-cosmic-900 rounded-lg hover:bg-divine-400 transition-colors"
              >
                Install
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="text-center">
              <h3 className="text-cream font-semibold text-sm mb-2">
                Add to Home Screen
              </h3>
              {platform.current === 'ios-safari' ? (
                <div className="text-cream/60 text-xs space-y-2 text-left">
                  <p>1. Tap the <span className="text-divine-400">Share</span> button at the bottom of the screen</p>
                  <p>2. Scroll down and tap <span className="text-divine-400">"Add to Home Screen"</span></p>
                  <p>3. Tap <span className="text-divine-400">"Add"</span> in the top right</p>
                </div>
              ) : (
                <div className="text-cream/60 text-xs space-y-2 text-left">
                  <p>1. Tap the <span className="text-divine-400">menu icon</span> (three dots) in your browser</p>
                  <p>2. Tap <span className="text-divine-400">"Add to Home Screen"</span> or <span className="text-divine-400">"Install App"</span></p>
                </div>
              )}
            </div>
            <button
              onClick={handleDismiss}
              className="w-full mt-3 px-3 py-2 text-xs text-cream/50 hover:text-cream/80 transition-colors rounded-lg"
            >
              Got it
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PWAInstallBanner;
